import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

export default function DealerNav() {
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_BASE;
  const token = useMemo(() => localStorage.getItem("dealerToken"), []);
  const [counts, setCounts] = useState({ total: 0, unread: 0 });

  // decode dealerId from JWT
  const dealerId = useMemo(() => {
    try {
      const part = token?.split(".")[1];
      if (!part) return null;
      const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
      const payload = JSON.parse(json);
      return payload.DealerId ?? payload.dealerId ?? null;
    } catch {
      return null;
    }
  }, [token]);

  const fetchCounters = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/dealer/leads/counters`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setCounts({ total: data.total ?? 0, unread: data.unread ?? 0 });
    } catch {}
  };

  // 🔁 Visibility-aware polling (replaces your previous useEffect)
  useEffect(() => {
    let timerId = null;

    const start = () => {
      if (timerId) clearInterval(timerId);
      // fetch immediately, then every 30s
      fetchCounters();
      timerId = setInterval(fetchCounters, 300000);
    };

    const stop = () => {
      if (timerId) clearInterval(timerId);
      timerId = null;
    };

    const onFocus = () => fetchCounters();
    const onVis = () => {
      if (document.visibilityState === "visible") start();
      else stop();
    };

    // kick off
    start();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);

    return () => {
      stop();
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const linkBase = "relative px-3 py-2 rounded-md text-sm font-medium";
  const active = "bg-blue-600 text-white";
  const inactive = "text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200";

  const logout = () => {
    localStorage.removeItem("dealerToken");
    navigate("/dealer/login", { replace: true });
  };

  return (
    <div className="w-full bg-white border-b mb-4">
      <div className="flex flex-wrap items-center gap-2 max-w-6xl mx-auto px-4 py-2">
        <NavLink to="/dealer/dashboard" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`}>
          Dashboard
        </NavLink>

        <NavLink
          to="/dealer/account/profile"
          className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`}
        >
          Profile
        </NavLink>

        <NavLink
          to="/dealer/leads"
          className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`}
        >
          Leads
          {counts.unread > 0 && (
            <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-red-600 text-[10px] text-white flex items-center justify-center">
              {counts.unread}
            </span>
          )}
        </NavLink>

        {dealerId && (
          <NavLink
            to={`/dealer/inventory`}
            className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`}
          >
            Inventory
          </NavLink>
        )}

        {dealerId && (
          <NavLink
            to="/dealer/inventory-intelligence"
            className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`}
          >
            Inventory Intelligence
          </NavLink>
        )}

        <NavLink
          to="/dealer/appraisal"
          className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`}
        >
          Appraisal
        </NavLink>

        <NavLink to="/dealer/invites" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`}>
          Invites
        </NavLink>

        <NavLink to="/dealer/staff" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`}>
          Staff
        </NavLink>

        <NavLink to="/dealer/settings" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`}>
          Settings
        </NavLink>

        <NavLink to="/dealer/account/password" className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`}>
          Password
        </NavLink>

        <button
          onClick={logout}
          className="ml-auto px-3 py-2 rounded-md text-sm border border-gray-300 hover:bg-gray-100"
        >
          Logoff
        </button>
      </div>
    </div>
  );
}
